import React, { type FC, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import axios, { type AxiosResponse } from 'axios';

import { Box } from '@clutter/clean';

import { Breadcrumb, Panel, Table, NavLinkButton } from '@shared/components/bootstrap';
import { InputFormControl } from '@shared/components/fields/input_form_control';
import { Pluralize } from '@shared/components/helpers';
import { Pagination } from '@shared/components/pagination';
import { useDebounce } from '@shared/hooks/use_debounce';
import { type IPagination } from '@shared/types/pagination';
import { UserRole } from '@shared/types/user_role';

import { PageHeader } from '@admin/components/helpers/page_header';
import { Roles } from '@admin/components/helpers/roles';
import { Titleize } from '@admin/components/helpers/titleize';
import { Spinner } from '@admin/components/spinner';
import { useAxiosRequest } from '@admin/hooks';
import { useTitle } from '@admin/hooks/use_title';

const DEFAULT_PAGE = 1;

type Barcode = {
  value: string;
};

type Warehouse = {
  id: number;
  name: string;
};

type Location = {
  id: number;
  barcode: Barcode;
  name: string;
  type: string;
  pallets_count: number;
  items_count: number;
  capacity: number;
  inactive: boolean;
  damaged: boolean;
  is_full: boolean;
  directable_kind: string;
};

type Paginated = {
  pagination: IPagination;
  results: Location[];
};

const useWarehouseRequest = ({ warehouseID }: { warehouseID: number | string }) => {
  const [request, setRequest] = useState<Promise<AxiosResponse<Warehouse>> | undefined>(undefined);
  useEffect(() => {
    setRequest(axios.get<Warehouse>(`/warehouses/${warehouseID}.json`));
  }, [warehouseID]);
  return useAxiosRequest(request);
};

const useWarehouseLocationsRequest = ({
  warehouseID,
  page,
  query,
}: {
  warehouseID: number | string;
  page: number;
  query?: string;
}) => {
  const [request, setRequest] = useState<Promise<AxiosResponse<Paginated>> | undefined>(undefined);

  useEffect(() => {
    setRequest(
      axios.get<Paginated>(`/warehouses/${warehouseID}/locations.json`, {
        params: {
          page,
          query,
        },
      }),
    );
  }, [warehouseID, page, query]);

  return useAxiosRequest(request);
};

export const LocationList: FC = () => {
  useTitle('Locations');
  const { warehouseID } = useParams<{ warehouseID: string }>();
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(DEFAULT_PAGE);
  const { response: responseForWarehouseRequest } = useWarehouseRequest({ warehouseID });
  const { response: responseForWarehouseLocationsRequest } = useWarehouseLocationsRequest({
    warehouseID,
    page,
    query: useDebounce(query || undefined),
  });
  const warehouse = responseForWarehouseRequest?.data;
  const pagination = responseForWarehouseLocationsRequest?.data?.pagination;
  const locations = responseForWarehouseLocationsRequest?.data?.results;

  return (
    <>
      <PageHeader>
        <Breadcrumb>
          <Breadcrumb.Item>
            <NavLink to="/warehouses">Warehouses</NavLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <NavLink to={`/warehouses/${warehouseID}`}>{warehouse?.name ?? '-'}</NavLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item active>Locations</Breadcrumb.Item>
        </Breadcrumb>
      </PageHeader>

      <Panel>
        <Panel.Body>
          <div className="mb-4">
            <InputFormControl
              type="search"
              placeholder="Search by barcode or name"
              value={query}
              onChange={(event) => {
                setPage(DEFAULT_PAGE);
                setQuery(event.target.value);
              }}
            />
          </div>
          <Table striped responsive>
            <thead>
              <tr>
                <th className="col-sm-2 text-center">Barcode</th>
                <th className="col-sm-1 text-center">Name</th>
                <th className="col-sm-1 text-center">Type</th>
                <th className="col-sm-1 text-center">Stats</th>
                <th className="col-sm-1 text-center">Capacity</th>
                <th className="col-sm-1 text-center">Inactive</th>
                <th className="col-sm-1 text-center">Damaged</th>
                <th className="col-sm-1 text-center">Is Full?</th>
                <th className="col-sm-1 text-center">Directable Kind</th>
                <th className="col-zero text-right">
                  <Roles show={[UserRole.Admin]}>
                    <NavLinkButton kind="mint" to={`/warehouses/${warehouseID}/locations/new`}>
                      Add
                    </NavLinkButton>
                  </Roles>
                </th>
              </tr>
            </thead>
            <tbody>
              {locations?.map((location) => (
                <tr key={location.id}>
                  <td className="col-sm-2 text-center">{location.barcode.value}</td>
                  <td className="col-sm-1 text-center">{location.name}</td>
                  <td className="col-sm-1 text-center">
                    <Titleize text={location.type} />
                  </td>
                  <td className="col-sm-1 text-center">
                    <div>
                      <Pluralize count={location.pallets_count} singular="pallet" plural="pallets" />
                    </div>
                    <div>
                      <Pluralize count={location.items_count} singular="item" plural="items" />
                    </div>
                  </td>
                  <td className="col-sm-1 text-center">{location.capacity ?? '-'}</td>
                  <td className="col-sm-1 text-center">{location.inactive ? 'Yes' : 'No'}</td>
                  <td className="col-sm-1 text-center">{location.damaged ? 'Yes' : 'No'}</td>
                  <td className="col-sm-1 text-center">{location.is_full ? 'Yes' : 'No'}</td>
                  <td className="col-sm-1 text-center">{location.directable_kind ?? '-'}</td>
                  <td className="col-zero text-right">
                    <Box.Flex gap="4px">
                      <NavLinkButton kind="primary" to={`/locations/${location.id}`}>
                        Details
                      </NavLinkButton>
                      <Roles show={[UserRole.Admin]}>
                        <NavLinkButton kind="primary" to={`/locations/${location.id}/edit`}>
                          Edit
                        </NavLinkButton>
                      </Roles>
                    </Box.Flex>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {pagination && <Pagination pagination={pagination} onPage={setPage} />}
          {!locations && <Spinner />}
        </Panel.Body>
      </Panel>
    </>
  );
};
